/**
 * WinterlagerPage — Jun-2026 (User-Wunsch).
 *
 * Übersicht der eingelagerten Fahrzeuge (Winterlager). Fahrzeuge landen
 * automatisch hier, sobald ein Werkstattauftrag mit einem „Winter-Einlagerung"-
 * Artikel abgeschlossen wird. Zeigt je Fahrzeug Einlagerungs-Datum, Kunde,
 * Fahrzeugdaten, Beschädigungen (+ Fotos) und die laufende Standgebühr-
 * Abrechnung. Auslagerung mit Datum möglich.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Snowflake, ArrowLeft, Loader2, Upload, Trash2, LogOut, Save, RefreshCcw, Plus, Car, FileText, Printer, Lock, TrendingUp, Mail,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { previewPdfResponse, customerRecipient } from "../../lib/pdfPreview";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";

const photoUrl = (path) => `${API}/wms/photos/${path}?auth=${sessionStorage.getItem("tw_token") || ""}`;
const eur = (n) => (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso || "—"; } };

function StorageCard({ rec, onChanged }) {
    const [note, setNote] = useState(rec.damage_note || "");
    const [fee, setFee] = useState(rec.monthly_fee_gross ?? 0);
    const [busy, setBusy] = useState(false);
    const [showRelease, setShowRelease] = useState(false);
    const released = rec.status === "released";
    const hasInvoice = Boolean(rec.storage_invoice_id);
    const invoiceFinalized = Boolean(rec.storage_invoice_finalized);

    const saveDetails = async () => {
        setBusy(true);
        try {
            await api.patch(`/winter-storage/${rec.id}`, { damage_note: note, monthly_fee_gross: Number(fee) || 0 });
            toast.success("Gespeichert.");
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const uploadPhoto = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/wms/photos/upload", fd);
            await api.post(`/winter-storage/${rec.id}/photos`, { path: data.path });
            toast.success("Schadensfoto hinzugefügt.");
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Upload fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const removePhoto = async (path) => {
        try { await api.delete(`/winter-storage/${rec.id}/photos`, { data: { path } }); onChanged?.(); }
        catch { toast.error("Foto konnte nicht entfernt werden."); }
    };

    const release = async (finalize) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/winter-storage/${rec.id}/release`, { finalize: !!finalize });
            const msg = data.invoice_finalized
                ? `Fahrzeug ausgelagert · Schlussrechnung finalisiert (Nr. ${data.invoice_number}).`
                : data.invoice_created
                    ? "Fahrzeug ausgelagert · Standgebühr als Entwurfs-Rechnung gebucht. (Im Filter „Ausgelagert“ finalisierbar.)"
                    : "Fahrzeug ausgelagert.";
            toast.success(msg);
            setShowRelease(false);
            onChanged?.();
        }
        catch (e) { toast.error(e.response?.data?.detail || "Auslagern fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const finalizeInvoice = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/winter-storage/${rec.id}/finalize-invoice`, {});
            toast.success(`Schlussrechnung finalisiert (Nr. ${data.invoice_number}).`);
            onChanged?.();
        }
        catch (e) { toast.error(e.response?.data?.detail || "Finalisieren fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const sendReminder = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/winter-storage/${rec.id}/send-reminder`, {});
            toast.success(data.email_mocked
                ? `Erinnerung vorbereitet (Demo-Modus, kein Live-Mailversand) → ${data.to}`
                : `Auslagerungs-Erinnerung an ${data.to} gesendet.`);
            onChanged?.();
        }
        catch (e) { toast.error(e.response?.data?.detail || "Erinnerung konnte nicht gesendet werden."); }
        finally { setBusy(false); }
    };

    const invoicePdf = async (mode) => {
        setBusy(true);
        try {
            const resp = await api.get(`/winter-storage/${rec.id}/invoice-pdf`, { responseType: "blob" });
            previewPdfResponse(resp, `Winterlager-Schlussrechnung-${rec.vehicle?.model || "Fahrzeug"}.pdf`, { recipient: customerRecipient(rec.customer) });
        } catch { toast.error("Schlussrechnung konnte nicht geladen werden."); }
        finally { setBusy(false); }
    };

    return (
        <div className={`border rounded-sm p-4 ${released ? "bg-gray-50 border-gray-200" : "bg-white border-cyan-200"}`} data-testid={`winter-card-${rec.id}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-cyan-700 shrink-0" />
                        <h3 className="font-display font-bold text-sm truncate">
                            {rec.vehicle?.manufacturer} {rec.vehicle?.model}
                        </h3>
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${released ? "bg-gray-200 text-gray-700" : "bg-cyan-100 text-cyan-800"}`}>
                            {released ? "Ausgelagert" : "Eingelagert"}
                        </span>
                        {!released && rec.release_overdue && (
                            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm shrink-0 bg-red-100 text-red-700 border border-red-300" data-testid={`winter-release-overdue-${rec.id}`}>Auslagerung überfällig</span>
                        )}
                        {!released && !rec.release_overdue && rec.release_due_soon && (
                            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm shrink-0 bg-amber-100 text-amber-800 border border-amber-300" data-testid={`winter-release-soon-${rec.id}`}>Auslagerung in {rec.planned_release_days} T.</span>
                        )}
                    </div>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                        <div><dt className="text-gray-500 inline">Eingelagert: </dt><dd className="inline font-semibold">{fmt(rec.stored_at)}</dd></div>
                        {!released && rec.planned_release_at && (
                            <div data-testid={`winter-planned-release-${rec.id}`}><dt className="text-gray-500 inline">Geplante Auslagerung: </dt><dd className={`inline font-semibold ${rec.release_overdue ? "text-red-700" : rec.release_due_soon ? "text-amber-700" : ""}`}>{fmt(rec.planned_release_at)}</dd></div>
                        )}
                        {released && <div><dt className="text-gray-500 inline">Ausgelagert: </dt><dd className="inline font-semibold">{fmt(rec.released_at)}</dd></div>}
                        <div><dt className="text-gray-500 inline">Kunde: </dt><dd className="inline font-semibold">{[rec.customer_first_name, rec.customer_last_name].filter(Boolean).join(" ") || "—"}</dd></div>
                        <div><dt className="text-gray-500 inline">Farbbasis: </dt><dd className="inline font-semibold">{rec.vehicle?.color_base || "—"}</dd></div>
                        <div className="col-span-2"><dt className="text-gray-500 inline">Fahrgestellnr.: </dt><dd className="inline font-mono-num font-semibold">{rec.vehicle?.vin || "—"}</dd></div>
                        {rec.last_release_reminder_at && (
                            <div className="col-span-2" data-testid={`winter-last-reminder-${rec.id}`}><dt className="text-cyan-700 inline">Zuletzt erinnert: </dt><dd className="inline font-semibold text-cyan-800">{fmt(rec.last_release_reminder_at)} ({rec.release_reminder_count || 1}×)</dd></div>
                        )}
                    </dl>
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Standgebühr</div>
                    <div className="font-black text-lg text-cyan-800 font-mono-num">{eur(rec.billed_amount_gross)}</div>
                    <div className="text-[10px] text-gray-500">{rec.billed_months} angef. Monat(e) × {eur(rec.monthly_fee_gross)}</div>
                </div>
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Vorhandene Beschädigungen</Label>
                    <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="z. B. Kratzer Tankdeckel links …" className="rounded-sm mt-1 text-sm" data-testid={`winter-damage-note-${rec.id}`} />
                </div>
                <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Standgebühr / Monat (€ brutto)</Label>
                    <Input type="number" step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid={`winter-fee-${rec.id}`} />
                </div>
            </div>

            {/* Schadensfotos */}
            <div className="mt-3">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Schadensfotos</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                    {(rec.damage_images || []).map((p) => (
                        <div key={p} className="relative group" data-testid={`winter-photo-${rec.id}`}>
                            <img src={photoUrl(p)} alt="Schaden" className="w-20 h-20 object-cover rounded-sm border border-gray-200" />
                            <button onClick={() => removePhoto(p)} className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" title="Foto entfernen">
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                    <label className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-sm flex items-center justify-center cursor-pointer hover:border-cyan-400 text-gray-400 hover:text-cyan-500" data-testid={`winter-photo-upload-${rec.id}`}>
                        <Upload className="w-5 h-5" />
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} disabled={busy} />
                    </label>
                </div>
            </div>

            <div className="flex flex-wrap justify-end items-center gap-2 mt-3 pt-3 border-t">
                {hasInvoice && (
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-sm mr-auto ${invoiceFinalized ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`} data-testid={`winter-invoice-status-${rec.id}`}>
                        {invoiceFinalized ? `Rechnung Nr. ${rec.storage_invoice_number || "—"} · finalisiert` : "Rechnung: Entwurf"}
                    </span>
                )}
                <Button onClick={saveDetails} disabled={busy} variant="outline" className="rounded-sm text-xs h-8" data-testid={`winter-save-${rec.id}`}>
                    <Save className="w-3.5 h-3.5 mr-1.5" /> Speichern
                </Button>
                {(released || hasInvoice) && (
                    <>
                        <Button onClick={() => invoicePdf("download")} disabled={busy} variant="outline" className="rounded-sm text-xs h-8" data-testid={`winter-invoice-${rec.id}`}>
                            <FileText className="w-3.5 h-3.5 mr-1.5" /> Schlussrechnung
                        </Button>
                        <Button onClick={() => invoicePdf("print")} disabled={busy} variant="outline" className="rounded-sm text-xs h-8" data-testid={`winter-invoice-print-${rec.id}`}>
                            <Printer className="w-3.5 h-3.5 mr-1.5" /> Drucken
                        </Button>
                    </>
                )}
                {hasInvoice && !invoiceFinalized && (
                    <Button onClick={finalizeInvoice} disabled={busy} className="rounded-sm bg-green-700 hover:bg-green-800 text-white font-bold text-xs h-8" data-testid={`winter-finalize-${rec.id}`}>
                        <Lock className="w-3.5 h-3.5 mr-1.5" /> Rechnung finalisieren
                    </Button>
                )}
                {!released && (
                    <Button onClick={sendReminder} disabled={busy} variant="outline" className="rounded-sm text-xs h-8 border-cyan-300 text-cyan-800" data-testid={`winter-reminder-${rec.id}`}>
                        <Mail className="w-3.5 h-3.5 mr-1.5" /> Erinnerung senden
                    </Button>
                )}
                {!released && (
                    <Button onClick={() => setShowRelease(true)} disabled={busy} className="rounded-sm bg-cyan-700 hover:bg-cyan-800 text-white font-bold text-xs h-8" data-testid={`winter-release-${rec.id}`}>
                        <LogOut className="w-3.5 h-3.5 mr-1.5" /> Auslagern
                    </Button>
                )}
            </div>

            {showRelease && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid={`winter-release-dialog-${rec.id}`}>
                    <div className="bg-white rounded-sm p-5 max-w-md w-full shadow-xl">
                        <h3 className="font-display font-black text-lg mb-1">Fahrzeug auslagern</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Standgebühr <span className="font-bold">{eur(rec.billed_amount_gross)}</span> ({rec.billed_months} angef. Monat(e)).
                            Wie soll die Schlussrechnung gebucht werden?
                        </p>
                        <div className="flex flex-col gap-2">
                            <Button onClick={() => release(true)} disabled={busy} className="rounded-sm bg-green-700 hover:bg-green-800 text-white font-bold h-10" data-testid={`winter-release-finalize-${rec.id}`}>
                                <Lock className="w-4 h-4 mr-2" /> Direkt finalisieren (GoBD-Nr.)
                            </Button>
                            <Button onClick={() => release(false)} disabled={busy} variant="outline" className="rounded-sm font-bold h-10" data-testid={`winter-release-draft-${rec.id}`}>
                                <FileText className="w-4 h-4 mr-2" /> Als Entwurf buchen
                            </Button>
                            <Button onClick={() => setShowRelease(false)} disabled={busy} variant="ghost" className="rounded-sm text-gray-500 h-9" data-testid={`winter-release-cancel-${rec.id}`}>
                                Abbrechen
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function WinterlagerPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("stored");
    const [storedCount, setStoredCount] = useState(0);
    const [summary, setSummary] = useState(null);
    const [bulkBusy, setBulkBusy] = useState(false);

    const bulkRemind = async () => {
        setBulkBusy(true);
        try {
            const { data } = await api.post("/winter-storage/send-reminders-bulk", {});
            toast.success(`${data.sent} Erinnerung(en) gesendet · ${data.skipped_no_email} ohne E-Mail übersprungen · ${data.already_sent_this_month} bereits diesen Monat.`);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Sammel-Versand fehlgeschlagen."); }
        finally { setBulkBusy(false); }
    };

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/winter-storage", { params: filter === "all" ? {} : { status: filter } });
            setItems(r.data.items || []);
            setStoredCount(r.data.stored_count ?? 0);
            setSummary(r.data.summary || null);
        } catch { toast.error("Winterlager konnte nicht geladen werden."); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        let active = true;
        api.get("/winter-storage", { params: filter === "all" ? {} : { status: filter } })
            .then((r) => { if (!active) return; setItems(r.data.items || []); setStoredCount(r.data.stored_count ?? 0); setSummary(r.data.summary || null); setLoading(false); })
            .catch(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [filter]);

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="winterlager-page">
            <Link to="/dealer/wms" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-4" data-testid="winter-back">
                <ArrowLeft className="w-4 h-4" /> Zur Warenwirtschaft
            </Link>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-2">
                    <Snowflake className="w-6 h-6 text-cyan-600" />
                    <h1 className="font-display text-2xl font-black text-[#111827]">Winterlager</h1>
                    <span className="text-sm text-gray-500">({storedCount} eingelagert)</span>
                </div>
                <div className="flex items-center gap-1.5">
                    {[["stored", "Eingelagert"], ["released", "Ausgelagert"], ["all", "Alle"]].map(([k, l]) => (
                        <button key={k} onClick={() => setFilter(k)}
                            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border ${filter === k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}
                            data-testid={`winter-filter-${k}`}>{l}</button>
                    ))}
                    <button onClick={load} className="text-gray-500 hover:text-gray-900 p-1.5" title="Aktualisieren" data-testid="winter-refresh"><RefreshCcw className="w-4 h-4" /></button>
                </div>
            </div>

            <div className="mb-4">
                <Button onClick={bulkRemind} disabled={bulkBusy} variant="outline" className="rounded-sm text-xs h-9 border-cyan-300 text-cyan-800 bg-white font-bold" data-testid="winter-bulk-reminder">
                    {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
                    Saison-Aktion: Erinnerung an alle senden
                </Button>
            </div>

            <p className="text-sm text-gray-600 mb-5">
                Folgende Fahrzeuge befinden sich zur Zeit im Winterlager. Fahrzeuge erscheinen automatisch, sobald ein Werkstattauftrag mit einem „Winter-Einlagerung“-Artikel abgeschlossen wird.
            </p>

            {summary && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="winter-revenue-summary">
                    <div className="border border-amber-200 bg-amber-50 rounded-sm p-3.5">
                        <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Offene Standgebühren</div>
                        <div className="font-black text-xl text-amber-800 font-mono-num mt-0.5" data-testid="winter-kpi-open">{eur(summary.open_fees_gross)}</div>
                        <div className="text-[10px] text-amber-600 mt-0.5">eingelagert, noch nicht abgerechnet</div>
                    </div>
                    <div className="border border-cyan-200 bg-cyan-50 rounded-sm p-3.5">
                        <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-bold">Gebuchter Umsatz</div>
                        <div className="font-black text-xl text-cyan-800 font-mono-num mt-0.5" data-testid="winter-kpi-billed">{eur(summary.billed_gross)}</div>
                        <div className="text-[10px] text-cyan-600 mt-0.5">Entwürfe + finalisierte Rechnungen</div>
                    </div>
                    <div className="border border-[#111827]/15 bg-gray-50 rounded-sm p-3.5">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Erwarteter Gesamtumsatz</div>
                        <div className="font-black text-xl text-[#111827] font-mono-num mt-0.5" data-testid="winter-kpi-total">{eur(summary.expected_total_gross)}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">offen + gebucht</div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex items-center gap-2 justify-center py-16 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> Lade Winterlager…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 text-gray-500 border-2 border-dashed border-gray-200 rounded-sm" data-testid="winter-empty">
                    <Snowflake className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    Keine Fahrzeuge {filter === "released" ? "ausgelagert" : "im Winterlager"}.
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4" data-testid="winter-list">
                    {items.map((rec) => <StorageCard key={rec.id} rec={rec} onChanged={load} />)}
                </div>
            )}
        </div>
    );
}
